import { useState } from 'react'

import { useCurrentStatusStore } from 'store/currentStatusStore'
import { getAPI } from 'utils/getAPI'

import type {
  CreateUserUserCreatePostRequest,
  ReadUsersUserSearchGetRequest,
  ResponseResult,
  ResponseUpdate,
  UpdateUserUserUpdatePostRequest,
  User,
} from 'api-client'
import type { ErrorResponse } from 'types/types'

export const useUser = () => {
  const [userSearch, setUserSearch] = useState<User>({
    datacount: 0,
    maxcount: 0,
    lastUpdateDatetime: null,
    list: [],
  })
  const [userSearchIsLoading, setUserSearchIsLoading] = useState(false)
  const [userCreateIsLoading, setUserCreateIsLoading] = useState(false)
  const [userUpdateIsLoading, setUserUpdateIsLoading] = useState(false)
  const [userSearchResult, setUserSearchResult] = useState<ResponseResult | undefined>(undefined)
  const [userCreateResult, setUserCreateResult] = useState<ResponseUpdate | undefined>(undefined)
  const [userUpdateResult, setUserUpdateResult] = useState<ResponseUpdate | undefined>(undefined)
  const { setCurrentStatuses } = useCurrentStatusStore()

  const fetchUserSearch = async (searchParam: ReadUsersUserSearchGetRequest) => {
    setUserSearchIsLoading(true)

    try {
      const api = await getAPI()
      if (!api) return undefined

      const response = await api.readUsersUserSearchGet(searchParam)

      setUserSearch(response.data)
      setUserSearchResult(response.result)
      setCurrentStatuses(response.result.systemInfo?.sysCurrentStatus)
      return response
    } catch (e) {
      const error = e as ErrorResponse
      setUserSearchResult({
        status: 'error',
        message: error.response && error.response.status === 422 ? '入力値に誤りがあります。' : `ユーザ一覧取得でエラーが発生しました。`,
        systemDate: null,
        pageNo: 0,
        systemInfo: null,
      })
      return undefined
    } finally {
      setUserSearchIsLoading(false)
    }
  }

  const fetchUserCreate = async (searchParam: CreateUserUserCreatePostRequest) => {
    setUserCreateIsLoading(true)

    try {
      const api = await getAPI()
      if (!api) return undefined

      const response = await api.createUserUserCreatePost(searchParam)

      setUserCreateResult(response)
      return response
    } catch (e) {
      const error = e as ErrorResponse
      setUserCreateResult({
        errors: [],
        result: {
          status: 'error',
          message: error.response && error.response.status === 422 ? '入力値に誤りがあります。' : `ユーザ作成でエラーが発生しました。`,
          systemDate: null,
          pageNo: 0,
          systemInfo: null,
        },
        updateCount: null,
        lastUpdateDatetime: null,
      })
      return undefined
    } finally {
      setUserCreateIsLoading(false)
    }
  }

  const fetchUserUpdate = async (searchParam: UpdateUserUserUpdatePostRequest) => {
    setUserUpdateIsLoading(true)

    try {
      const api = await getAPI()
      if (!api) return undefined

      const response = await api.updateUserUserUpdatePost(searchParam)

      setUserUpdateResult(response)
      return response
    } catch (e) {
      const error = e as ErrorResponse
      setUserUpdateResult({
        errors: [],
        result: {
          status: 'error',
          message: error.response && error.response.status === 422 ? '入力値に誤りがあります。' : `ユーザ更新でエラーが発生しました。`,
          systemDate: null,
          pageNo: 0,
          systemInfo: null,
        },
        updateCount: null,
        lastUpdateDatetime: null,
      })
      return undefined
    } finally {
      setUserUpdateIsLoading(false)
    }
  }

  return {
    userSearch,
    fetchUserSearch,
    userSearchResult,
    userSearchIsLoading,

    fetchUserCreate,
    userCreateResult,
    userCreateIsLoading,

    fetchUserUpdate,
    userUpdateResult,
    userUpdateIsLoading,
  }
}
